import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * "Paydaai'a Borcunuz" widget for admin dashboard.
 * Shows pending/overdue subscription_payments to Paydaai.
 * "Ödeme Yap" button redirects DIRECTLY to PayTR ödeme for the oldest unpaid debt.
 */
export const PaydaaiDebtCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/subscription/my-debts`, { withCredentials: true });
        setData(data);
      } catch (_e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [API_URL]);

  const handlePay = async () => {
    if (!data?.items || data.items.length === 0) return;
    // Pay the oldest pending debt first
    const debt = data.items[0];
    setPaying(true);
    try {
      const { data: res } = await axios.post(
        `${API_URL}/api/subscription/pay-debt`,
        { debt_id: debt.id, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (res.url) {
        window.location.href = `/checkout?title=${encodeURIComponent('Abonelik Borcu Ödemesi')}&return=${encodeURIComponent('/admin/dashboard')}&url=${encodeURIComponent(res.url)}`;
      } else {
        toast.error('Ödeme sayfası açılamadı');
        setPaying(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ödeme başlatılamadı');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-[#E5E5E5] col-span-full" data-testid="paydaai-debt-loading">
        <CardContent className="p-6 flex items-center gap-3 text-[#595959]">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Paydaai abonelik durumu yükleniyor...</span>
        </CardContent>
      </Card>
    );
  }

  // No debts
  if (!data || data.count === 0) return null;

  const overdueAmount = data.total_overdue || 0;
  const pendingAmount = data.total_pending || 0;
  const total = overdueAmount + pendingAmount;
  const hasOverdue = overdueAmount > 0;

  const fmt = (n) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString('tr-TR');
    } catch (_e) {
      return iso;
    }
  };

  return (
    <Card
      className={`col-span-full border-2 ${hasOverdue ? 'border-red-200 bg-red-50/30' : 'border-amber-200 bg-amber-50/30'}`}
      data-testid="paydaai-debt-card"
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasOverdue ? 'bg-red-100' : 'bg-amber-100'}`}>
              {hasOverdue
                ? <AlertCircle className="w-5 h-5 text-red-600" />
                : <CreditCard className="w-5 h-5 text-amber-700" />}
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1A1A2E]">
                Paydaai'a Abonelik Borcunuz
              </p>
              <p className="text-xs text-[#595959] mt-0.5">
                Platform kullanım ücreti · {data.count} açık kayıt
              </p>
              <div className="flex items-baseline gap-2 mt-3">
                <span className={`text-3xl font-bold ${hasOverdue ? 'text-red-700' : 'text-amber-800'}`} data-testid="paydaai-debt-total">
                  ₺{fmt(total)}
                </span>
                {hasOverdue && (
                  <span className="text-xs font-medium text-red-600 uppercase tracking-wide">
                    {fmt(overdueAmount)} ₺ gecikmiş
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button
            onClick={handlePay}
            disabled={paying}
            className={hasOverdue ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#2563EB] hover:bg-[#1E4ED8] text-white'}
            data-testid="paydaai-debt-pay-btn"
          >
            {paying ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ödemeye Yönlendiriliyor...</>
            ) : (
              <><CreditCard className="w-4 h-4 mr-2" /> Ödeme Yap</>
            )}
          </Button>
        </div>

        {data.items && data.items.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#E5E5E5]/50 space-y-1.5">
            {data.items.slice(0, 3).map((it) => {
              const isExtra = it.type === 'extra_charge';
              const label = isExtra
                ? (it.title || it.plan_label || 'Ek Ücret')
                : (it.plan_label || 'Abonelik');
              return (
                <div key={it.id} className="flex items-center justify-between text-xs" data-testid={`paydaai-debt-row-${it.id}`}>
                  <span className="text-[#595959]">
                    {isExtra && (
                      <span className="inline-block bg-blue-50 text-blue-700 border border-blue-100 rounded-sm px-1.5 py-0.5 text-[10px] font-medium mr-1.5 align-middle">Ek Ücret</span>
                    )}
                    {label}
                    {!isExtra && (<> · Vade: {fmtDate(it.due_date)}</>)}
                    {it.status === 'overdue' && it.days_overdue > 0 && (
                      <span className="ml-2 text-red-600 font-medium">({it.days_overdue} gün gecikmiş)</span>
                    )}
                  </span>
                  <span className="font-mono font-medium">₺{fmt(it.amount)}</span>
                </div>
              );
            })}
            {data.items.length > 3 && (
              <p className="text-xs text-[#999]">ve {data.items.length - 3} diğer kayıt...</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
